import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, Loader2, Check, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

/** Dispo Studio · Operator notification prefs for public offers.
 *  Fires on every `POST /api/public/deals/{slug}/offers` submission. */
export default function OfferNotificationsCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localPhone, setLocalPhone] = useState("");

  useEffect(() => {
    api.get("/notification-prefs")
      .then((r) => {
        setPrefs(r.data);
        setLocalPhone(r.data?.notify_offer_phone || "");
      })
      .catch(() => toast.error("Couldn't load offer notification prefs"));
  }, []);

  const patch = async (updates) => {
    const prev = prefs;
    const next = { ...prefs, ...updates };
    setPrefs(next);
    setSaving(true);
    try {
      const { data } = await api.patch("/notification-prefs", updates);
      setPrefs(data);
    } catch (err) {
      setPrefs(prev);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const savePhone = async () => {
    const trimmed = (localPhone || "").trim();
    if (trimmed && trimmed !== (prefs?.notify_offer_phone || "")) {
      await patch({ notify_offer_phone: trimmed });
      toast.success("SMS number saved");
    } else if (!trimmed && prefs?.notify_offer_phone) {
      await patch({ notify_offer_phone: "" });
    }
  };

  if (!prefs) {
    return (
      <div className="card" data-testid="settings-offer-notifications-card">
        <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Bell size={11} /> Offer notifications</div>
        <div className="text-sm text-[var(--ink-3)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="settings-offer-notifications-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-3">
        <Bell size={11} /> Offer notifications
      </div>
      <p className="text-xs text-[var(--ink-3)] mb-3">
        Get pinged the second a cash buyer submits an offer on any of your public Deal Rooms. First reply usually wins.
      </p>

      <div className="divide-y divide-[var(--line)]">
        <label className="flex items-start justify-between gap-3 py-2 cursor-pointer" data-testid="offer-notify-email-row">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Mail size={12} /> Email me</div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">Buyer name, price, EMD, close-by, and a one-click link to review in your Deal Room.</div>
          </div>
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[var(--primary)] cursor-pointer"
            checked={!!prefs.notify_offer_email}
            onChange={(e) => patch({ notify_offer_email: e.target.checked })}
            data-testid="offer-notify-email-toggle"
          />
        </label>

        <label className="flex items-start justify-between gap-3 py-2 cursor-pointer" data-testid="offer-notify-sms-row">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><MessageSquare size={12} /> Text me too</div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">A short SMS ping so you can react from your phone. Requires a verified Twilio sender.</div>
          </div>
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[var(--primary)] cursor-pointer"
            checked={!!prefs.notify_offer_sms}
            onChange={(e) => patch({ notify_offer_sms: e.target.checked })}
            data-testid="offer-notify-sms-toggle"
          />
        </label>
      </div>

      {prefs.notify_offer_sms && (
        <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="offer-notify-sms-config">
          <label htmlFor="offer-notify-phone" className="text-xs text-[var(--ink-3)] mb-1 block">
            SMS number (E.164 format, e.g. +12155550101)
          </label>
          <div className="flex items-center gap-2">
            <input
              id="offer-notify-phone"
              type="tel"
              value={localPhone}
              onChange={(e) => setLocalPhone(e.target.value)}
              onBlur={savePhone}
              placeholder="+12155550101"
              className="input flex-1"
              maxLength={20}
              data-testid="offer-notify-phone-input"
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--line)]">
        <div className="text-xs text-[var(--ink-3)] flex items-center gap-1">
          {saving
            ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
            : <><Check size={12} className="text-emerald-600" /> Auto-saved</>}
        </div>
      </div>
    </div>
  );
}
